import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const CalendarFilter = ({ isOpen, onClose, onApply, triggerRef }) => {
  const [activeTab, setActiveTab] = useState('Date');
  const [singleDate, setSingleDate] = useState(formatDate(new Date()));
  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [position, setPosition] = useState(null); // เปลี่ยนเป็น null

  useEffect(() => {
    if (isOpen && triggerRef?.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const popupWidth = 320;
      const popupHeight = 500; // ประมาณความสูงของ popup
      
      // คำนวณตำแหน่ง - แสดงด้านล่างปุ่ม
      let top = rect.bottom + 8;
      let left = rect.left;
      
      // ถ้าเกินขอบขวา ให้ชิดขวา
      if (left + popupWidth > window.innerWidth) {
        left = window.innerWidth - popupWidth - 16;
      }
      
      // ถ้าเกินขอบล่าง ให้แสดงด้านบน
      if (top + popupHeight > window.innerHeight) {
        top = rect.top - popupHeight - 8;
      }
      
      // ถ้า top ติดลบ (เกินด้านบน) ให้แสดงด้านล่างแทน
      if (top < 0) {
        top = rect.bottom + 8;
      }
      
      setPosition({ top, left });
    } else {
      setPosition(null); // reset เมื่อปิด
    }
  }, [isOpen, triggerRef]);

  // ไม่แสดง popup จนกว่า position จะพร้อม
  if (!isOpen || !position) return null;

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDayClick = (day) => {
    const selectedDate = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    if (activeTab === 'Date') {
      setSingleDate(selectedDate);
    } else {
      if (!startDate || (startDate && endDate)) {
        setStartDate(selectedDate);
        setEndDate('');
      } else {
        if (new Date(selectedDate) > new Date(startDate)) {
          setEndDate(selectedDate);
        } else {
          setEndDate(startDate);
          setStartDate(selectedDate);
        }
      }
    }
  };

  const handleApply = () => {
    if (activeTab === 'Date') {
      onApply({ startDate: singleDate, endDate: singleDate });
    } else {
      onApply({ startDate, endDate });
    }
    onClose();
  };

  const isDateSelected = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    if (activeTab === 'Date') {
      return dateStr === singleDate;
    } else {
      return dateStr === startDate || dateStr === endDate;
    }
  };

  const isDateInRange = (day) => {
    if (activeTab !== 'Range' || !startDate || !endDate) return false;
    
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const date = new Date(dateStr);
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return date > start && date < end;
  };

  const renderCalendar = () => {
    const days = [];
    
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="h-9"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = isDateSelected(day);
      const isInRange = isDateInRange(day);
      
      days.push(
        <button
          key={day}
          onClick={() => handleDayClick(day)}
          className={`h-9 w-9 flex items-center justify-center rounded-xl text-sm transition-colors ${
            isSelected
              ? 'bg-black text-white font-medium'
              : isInRange
              ? 'bg-gray-300 text-gray-900'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <>
      <div 
        className="fixed inset-0 z-[100]"
        onClick={onClose}
      ></div>
      <div 
        className="fixed bg-white rounded-xl shadow-2xl z-[101]"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: '320px',
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          opacity: position ? 1 : 0,
          transition: 'opacity 0.15s ease-in-out'
        }}
      >
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-900">go to...</h3>
        </div>
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('Date')}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'Date'
                ? 'text-gray-900 border-b-2 border-black'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Date
          </button>
          <button
            onClick={() => setActiveTab('Range')}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'Range'
                ? 'text-gray-900 border-b-2 border-black'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Range
          </button>
        </div>
        <div className="px-4 py-3">
          {activeTab === 'Date' ? (
            <div className="mb-3">
              <div className="flex items-center gap-2 border border-blue-500 rounded-xl px-3 py-1.5 bg-white">
                <input
                  type="date"
                  value={singleDate}
                  onChange={(e) => setSingleDate(e.target.value)}
                  className="flex-1 text-sm focus:outline-none date-input"
                />
              </div>
            </div>
          ) : (
            <div className="mb-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Start</label>
                  <div className="flex items-center border border-blue-500 rounded-xl px-2 py-1.5 bg-white">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full text-sm focus:outline-none date-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">End</label>
                  <div className="flex items-center border border-blue-500 rounded-xl px-2 py-1.5 bg-white">
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full text-sm focus:outline-none date-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={handlePrevMonth}
              className="p-1 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <span className="text-sm font-medium text-gray-900">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </span>
            <button
              onClick={handleNextMonth}
              className="p-1 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
          <div className="mb-3">
            <div className="grid grid-cols-7 gap-1 mb-1">
              {weekDays.map((day) => (
                <div
                  key={day}
                  className="h-7 flex items-center justify-center text-xs font-medium text-gray-500 bg-gray-100 rounded-xl"
                >
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {renderCalendar()}
            </div>
          </div>
        </div>
        <div className="h-px bg-gray-200 mx-4"></div>
        <div className="flex items-center justify-end gap-2 px-4 py-3">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
          >
            cancel
          </button>
          <button
            onClick={handleApply}
            className="px-5 py-1.5 text-sm font-medium text-white bg-black rounded-xl hover:bg-gray-800 transition-colors"
          >
            go
          </button>
        </div>
      </div>
    </>
  );
};

export default CalendarFilter;